// src/Components/Promo/PortfolioDocx.js

import { asBlob } from 'html-docx-js-typescript';
import { saveAs } from 'file-saver';

// --- CONSTANTS ---
const MM_TO_PX = 3.78;
const RENDER_SCALE = 3;

// --- HELPERS ---

const applyOpacityToHex = (hex, opacityPercent) => {
  if (!hex) return '#F5F5F5';
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length !== 6) return hex;

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  const opacity = Math.max(0, Math.min(100, opacityPercent)) / 100;

  // Blend with white background for Word DOCX compatibility
  const blendedR = Math.round(r * opacity + 255 * (1 - opacity));
  const blendedG = Math.round(g * opacity + 255 * (1 - opacity));
  const blendedB = Math.round(b * opacity + 255 * (1 - opacity));

  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(blendedR)}${toHex(blendedG)}${toHex(blendedB)}`.toUpperCase();
};

const processLogoImage = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        const img = new Image();
        img.onload = () => {
          resolve({
            base64: base64,
            width: img.naturalWidth,
            height: img.naturalHeight,
            aspectRatio: img.naturalWidth / img.naturalHeight,
          });
        };
        img.src = base64;
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Logo load failed', error);
    return null;
  }
};

const cropToSquareHighRes = async (
  url,
  targetDisplaySizePx,
  useRoundedCorners,
  roundedCornerRadius,
) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();

    const img = await new Promise((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.src = URL.createObjectURL(blob);
    });

    const size = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - size) / 2;
    const sy = (img.naturalHeight - size) / 2;

    const renderSize = targetDisplaySizePx * RENDER_SCALE;
    const finalRenderSize = Math.min(renderSize, size);

    const canvas = document.createElement('canvas');
    canvas.width = finalRenderSize;
    canvas.height = finalRenderSize;
    const ctx = canvas.getContext('2d');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (useRoundedCorners && roundedCornerRadius) {
      let effectiveRadius = roundedCornerRadius * (finalRenderSize / targetDisplaySizePx);
      effectiveRadius = Math.min(effectiveRadius, finalRenderSize / 2);

      ctx.beginPath();
      ctx.moveTo(effectiveRadius, 0);
      ctx.lineTo(finalRenderSize - effectiveRadius, 0);
      ctx.quadraticCurveTo(finalRenderSize, 0, finalRenderSize, effectiveRadius);
      ctx.lineTo(finalRenderSize, finalRenderSize - effectiveRadius);
      ctx.quadraticCurveTo(
        finalRenderSize,
        finalRenderSize,
        finalRenderSize - effectiveRadius,
        finalRenderSize,
      );
      ctx.lineTo(effectiveRadius, finalRenderSize);
      ctx.quadraticCurveTo(0, finalRenderSize, 0, finalRenderSize - effectiveRadius);
      ctx.lineTo(0, effectiveRadius);
      ctx.quadraticCurveTo(0, 0, effectiveRadius, 0);
      ctx.closePath();
      ctx.clip();
    }

    ctx.drawImage(img, sx, sy, size, size, 0, 0, finalRenderSize, finalRenderSize);

    // PNG is required if corners are rounded to preserve transparency, JPEG is smaller for square images
    return useRoundedCorners
      ? canvas.toDataURL('image/png')
      : canvas.toDataURL('image/jpeg', 0.95);
  } catch (error) {
    console.warn('Photo crop failed', error);
    return null;
  }
};

const stripDefaultBlackColors = (html) => {
  if (!html) return '';
  return html.replace(
    /(^|[\s;"'])color\s*:\s*(?:#000000|#000|rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*(?:1\vert{}1\.0+)\s*\)|black|windowtext)\s*;?/gi,
    '$1',
  );
};

// Safely injects muted grey (#7F7F7F) into blocks without explicit non-black colors
const applyMutedGreyFallback = (htmlString) => {
  if (!htmlString) return '';
  return htmlString.replace(/<(p|li)([^>]*)>/gi, (match, tag, attributes) => {
    if (/(?:^|[\s;"'])color\s*:/i.test(attributes)) {
      return match;
    }
    if (attributes.toLowerCase().includes('style="')) {
      return `<${tag}${attributes.replace(/style="/i, 'style="color: #7F7F7F; ')}>`;
    }
    if (attributes.toLowerCase().includes("style='")) {
      return `<${tag}${attributes.replace(/style='/i, "style='color: #7F7F7F; ")}>`;
    }
    return `<${tag}${attributes} style="color: #7F7F7F;">`;
  });
};

// Ensures any loose/unwrapped text outside of block-level elements is wrapped in <p>...</p>
const wrapLooseTextInParagraphs = (htmlFragment) => {
  if (!htmlFragment || !htmlFragment.trim()) return '';

  const blockRegex =
    /(<(?:p|div|h[1-6]|ul|ol|table|blockquote)[\s>][\s\S]*?<\/(?:p|div|h[1-6]|ul|ol|table|blockquote)>)/gi;
  const parts = htmlFragment.split(blockRegex);

  return parts
    .map((part) => {
      if (!part || !part.trim()) return '';
      if (/^<(?:p|div|h[1-6]|ul|ol|table|blockquote)[\s>]/i.test(part.trim())) {
        return part;
      }
      const lines = part
        .split(/<br\s*\/?>|\r?\n/i)
        .map((l) => l.trim())
        .filter(Boolean);
      return lines.map((l) => `<p>${l}</p>`).join('');
    })
    .join('');
};

// Formats the description so that:
// 1. The first paragraph (title) keeps its RTE colour or defaults to #000000 (black).
// 2. If the first paragraph contains <br> tags (Shift+Enter / plain-text newlines),
//    only the first line is the black title and subsequent lines become grey body text.
// 3. Any loose/unwrapped text after the title <p> is wrapped in <p> tags.
// 4. Body paragraphs and list items have default black inline styles stripped so they
//    reliably receive #7F7F7F unless a non-black colour was explicitly assigned in RTE.
const formatDescriptionColors = (rawHtml) => {
  if (!rawHtml) return '';

  let processed = rawHtml
    .replace(/\u00A0/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  // Strip out empty paragraphs at the start that intercept the title logic
  processed = processed.replace(/^(<p[^>]*>(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*)+/i, '');

  // If the description does not start with a block tag, wrap loose text in <p> first
  if (!/^<(p|div|h[1-6]|ul|ol|li|table|blockquote)[\s>]/i.test(processed)) {
    processed = wrapLooseTextInParagraphs(processed);
  }

  // Match the first <p>...</p> as the title paragraph
  const firstPMatch = processed.match(/^(\s*<p([^>]*)>([\s\S]*?)<\/p>)/i);
  if (firstPMatch) {
    const fullFirstP = firstPMatch[1];
    const firstPAttrs = firstPMatch[2] || '';
    const firstPInner = firstPMatch[3] || '';
    let remainder = processed.slice(fullFirstP.length);

    let titleInner = firstPInner;
    const brSplitMatch = firstPInner.match(/^(.*?)<br\s*\/?>([\s\S]*)$/i);
    if (brSplitMatch && brSplitMatch[2].replace(/<[^>]+>/g, '').trim().length > 0) {
      titleInner = brSplitMatch[1];
      remainder = `<p>${brSplitMatch[2]}</p>` + remainder;
    }

    // Wrap any loose/unwrapped text in remainder (such as raw text after the title </p>) in <p> tags
    remainder = wrapLooseTextInParagraphs(remainder);

    // Check if an explicit colour is already set on the first <p> or inside it
    const hasExplicitTitleColor =
      /(?:^|[\s;"'])color\s*:/i.test(firstPAttrs) ||
      /(?:^|[\s;"'])color\s*:/i.test(titleInner);

    let newFirstPAttrs = firstPAttrs;
    if (!hasExplicitTitleColor) {
      if (/style="/i.test(newFirstPAttrs)) {
        newFirstPAttrs = newFirstPAttrs.replace(/style="/i, 'style="color: #000000; ');
      } else if (/style='/i.test(newFirstPAttrs)) {
        newFirstPAttrs = newFirstPAttrs.replace(/style='/i, "style='color: #000000; ");
      } else {
        newFirstPAttrs += ' style="color: #000000;"';
      }
    }

    const formattedFirstP = `<p${newFirstPAttrs}>${titleInner}</p>`;
    const cleanedRemainder = applyMutedGreyFallback(stripDefaultBlackColors(remainder));
    return formattedFirstP + cleanedRemainder;
  }

  return applyMutedGreyFallback(
    stripDefaultBlackColors(wrapLooseTextInParagraphs(processed)),
  );
};

// Estimates the rendered vertical height (in points) of a description block inside the Word table cell
// to determine whether the row can fit on a single page or must be allowed to split across pages.
const estimateDocxRowHeightPt = (
  html,
  standardSize,
  colWidthPt,
  photoSizePt,
  isHilight,
) => {
  // Word page width (595.28pt) minus 1 inch (72pt) left/right margins (720 twips each) = 523.28pt
  const textColWidthPt = Math.max(150, 523.28 - colWidthPt);
  const avgCharWidthPt = standardSize * 0.58;
  const charsPerLine = Math.max(20, Math.floor(textColWidthPt / avgCharWidthPt));

  const blockMatches = [...html.matchAll(/<(p|li|h[1-6])([^>]*)>([\s\S]*?)<\/\1>/gi)];

  let totalTextHeightPt = 0;

  if (blockMatches.length > 0) {
    blockMatches.forEach((m) => {
      const tag = (m[1] || 'p').toLowerCase();
      const attrs = m[2] || '';
      const inner = m[3] || '';

      let blockFontSize = standardSize;
      const sizeMatches = [
        ...attrs.matchAll(/font-size:\s*(\d+(?:\.\d+)?)(?:pt|px)/gi),
        ...inner.matchAll(/font-size:\s*(\d+(?:\.\d+)?)(?:pt|px)/gi),
      ];
      if (sizeMatches.length > 0) {
        blockFontSize = Math.max(
          standardSize,
          ...sizeMatches.map((sm) => parseFloat(sm[1])),
        );
      }

      const blockCharsPerLine =
        tag === 'li'
          ? Math.max(15, Math.floor((textColWidthPt - 20) / (blockFontSize * 0.58)))
          : Math.max(20, Math.floor(textColWidthPt / (blockFontSize * 0.58)));

      const blockLineHeightPt = blockFontSize * 1.28;

      const subLines = inner.split(/<br\s*\/?>/i);
      let blockLines = 0;
      subLines.forEach((sl) => {
        const plain = sl
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        blockLines += Math.max(1, Math.ceil(plain.length / blockCharsPerLine));
      });
      totalTextHeightPt += blockLines * blockLineHeightPt + 3.5;
    });
  } else {
    const plain = html
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const lines = Math.max(1, Math.ceil(plain.length / charsPerLine));
    totalTextHeightPt = lines * (standardSize * 1.28);
  }

  // Account for .rte-content 8pt top/bottom margins (16pt) + optional hilight padding
  const hilightPaddingPt = isHilight ? 24 : 0;
  return Math.max(photoSizePt, totalTextHeightPt) + 16 + hilightPaddingPt;
};

export const generatePortfolioDocx = async (data) => {
  try {
    const {
      title,
      subjectTitle,
      regionText,
      categoryText,
      dateText,
      batchText,
      keyword,
      infoFilterA,
      infoFilterB,
      logo,
      photoDimension,
      items,
      useRoundedCorners,
      roundedCornerRadius,
      isMultipleRegions,
      defaultFontType = 'Arial',
      defaultFontSize = '11pt',
      hilightColor,
      hilightOpacity,
    } = data;

    const displayTitle = subjectTitle || title || '';
    const fallbackFont = `${defaultFontType}, Arial, sans-serif`;
    const standardSizePt =
      parseInt((defaultFontSize || '11pt').replace(/[^0-9]/g, ''), 10) || 11;

    const logoDisplayWidth = Math.round(60 * MM_TO_PX);
    const headerLeftColWidth = Math.round(70 * MM_TO_PX);

    const photoWidthMm = parseInt(photoDimension) || 50;
    const photoDisplaySize = Math.round(photoWidthMm * MM_TO_PX);
    const contentLeftColWidth = photoDisplaySize + 20;

    const photoSizePt = photoWidthMm * 2.835;
    const colWidthPt = photoSizePt + 20;
    const SINGLE_PAGE_MAX_HEIGHT_PT = 650;

    const logoData = await processLogoImage(logo);
    let logoHtml = '[LOGO]';

    if (logoData) {
      const logoDisplayHeight = Math.round(logoDisplayWidth / logoData.aspectRatio);
      logoHtml = `<img src="${logoData.base64}" width="${logoDisplayWidth}" height="${logoDisplayHeight}" style="width:${logoDisplayWidth}px; height:${logoDisplayHeight}px; display: block;" />`;
    }

    // Prepare valid items and inject required rendering state
    const validItems = [];
    for (const item of items) {
      const firstMedia = item.photos && item.photos.length > 0 ? item.photos[0] : null;
      const firstImageMedia = (item.photos || []).find((p) => {
        if (p.type) return p.type === 'image';
        const str = (p.fileName || p.url || '').toLowerCase();
        return !str.match(/\.(mp4|mp3|wav|m4a|aac|mov|avi|webm)$/i);
      });

      const imageUrl = firstImageMedia ? firstImageMedia.url : null;
      let rawText = '';
      if (firstImageMedia && firstImageMedia.description) {
        rawText = firstImageMedia.description;
      } else if (firstMedia && firstMedia.description) {
        rawText = firstMedia.description;
      }

      const strippedText = rawText
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, '')
        .trim();
      const hasText = strippedText.length > 0;

      if (imageUrl || hasText) {
        validItems.push({
          ...item,
          _imageUrl: imageUrl,
          _rawText: rawText,
          _hasText: hasText,
        });
      }
    }

    // Grouping by Region Name
    const groupedItems = {};
    if (isMultipleRegions) {
      validItems.forEach((item) => {
        const rName = item.regionName || 'Other';
        if (!groupedItems[rName]) groupedItems[rName] = [];
        groupedItems[rName].push(item);
      });
    } else {
      groupedItems['default'] = validItems;
    }

    const sortedRegionNames = isMultipleRegions
      ? Object.keys(groupedItems).sort((a, b) => a.localeCompare(b))
      : ['default'];

    const showRegionHeaders = isMultipleRegions && sortedRegionNames.length > 1;

    let rowsHtml = '';
    let globalItemCount = 0;

    const finalHilightColor = applyOpacityToHex(
      hilightColor || '#F5F5F5',
      hilightOpacity !== undefined ? hilightOpacity : 100,
    );

    // Loop through regions then items
    for (const regionName of sortedRegionNames) {
      if (showRegionHeaders) {
        rowsHtml += `
          <div style="padding-top: 12pt; padding-bottom: 6pt; page-break-inside: avoid; page-break-after: avoid;">
            <p style="font-family: ${fallbackFont}; font-size: 12pt; font-weight: bold; margin: 0; color: #000000;">${regionName}</p>
          </div>
        `;
      }

      for (const item of groupedItems[regionName]) {
        const imageUrl = item._imageUrl;
        const rawText = item._rawText;
        const hasText = item._hasText;
        const isHilight = item.hilight === true || item.Hilight === true;

        let targetDescription = '<p style="color: #7F7F7F;">No description provided.</p>';
        if (hasText) {
          targetDescription = formatDescriptionColors(rawText);
        }

        // Determine if this row exceeds the printable height of a single page.
        // If it fits on one page, apply page-break-inside: avoid so Word moves the whole row to a new page if needed.
        // If it exceeds one page, omit page-break-inside: avoid so Word allows the row to split across pages with proper bottom margins.
        const estimatedHeightPt = estimateDocxRowHeightPt(
          targetDescription,
          standardSizePt,
          colWidthPt,
          photoSizePt,
          isHilight,
        );
        const exceedsOnePage = estimatedHeightPt > SINGLE_PAGE_MAX_HEIGHT_PT;
        const isFirstGlobalItem = globalItemCount === 0;

        const pageBreakStyle = exceedsOnePage
          ? isFirstGlobalItem
            ? ''
            : 'page-break-before: always;'
          : 'page-break-inside: avoid;';
        const trBreakStyle = exceedsOnePage ? '' : ' style="page-break-inside: avoid;"';

        const borderRadiusStyle =
          useRoundedCorners && roundedCornerRadius
            ? `border-radius: ${roundedCornerRadius}px; `
            : '';

        let imgContentLeft = '';
        let imgContentRight = '';

        if (imageUrl) {
          const squareBase64 = await cropToSquareHighRes(
            imageUrl,
            photoDisplaySize,
            useRoundedCorners,
            roundedCornerRadius,
          );
          if (squareBase64) {
            imgContentLeft = `
              <p style="margin-top: 8pt; margin-bottom: 8pt; padding: 0;">
                <img src="${squareBase64}" width="${photoDisplaySize}" height="${photoDisplaySize}" style="width:${photoDisplaySize}px; height:${photoDisplaySize}px; display: block; ${borderRadiusStyle}" />
              </p>`;
            imgContentRight = `
              <p style="margin-top: 8pt; margin-bottom: 8pt; padding: 0; text-align: right;" align="right">
                <img src="${squareBase64}" width="${photoDisplaySize}" height="${photoDisplaySize}" style="width:${photoDisplaySize}px; height:${photoDisplaySize}px; display: inline-block; ${borderRadiusStyle}" />
              </p>`;
          }
        }

        let tableStyle = `width: 100%; margin-bottom: 16pt; border-collapse: collapse; ${pageBreakStyle}`;
        let vPadding = '';
        let td1Style = '';
        let td2Style = '';

        const isEven = globalItemCount % 2 === 0;
        globalItemCount++;

        if (isEven) {
          td1Style = `width: ${contentLeftColWidth}px; vertical-align: top; padding-right: 20px;`;
          td2Style = `vertical-align: top;`;
        } else {
          td1Style = `vertical-align: top; padding-right: 20px;`;
          // AMENDED: Applied text-align: right to explicitly push the RHS image flush right
          td2Style = `width: ${contentLeftColWidth}px; vertical-align: top; text-align: right;`;
        }

        if (isHilight) {
          const bRadius =
            useRoundedCorners && roundedCornerRadius
              ? `border-radius: ${roundedCornerRadius}px; `
              : '';

          // Background applies to the whole table, capturing the empty spacer columns to create left/right padding
          tableStyle = `width: 100%; margin-bottom: 16pt; margin-top: 8pt; background-color: ${finalHilightColor}; border: 1px solid #CCCCCC; border-collapse: collapse; ${pageBreakStyle} ${bRadius}`;
          vPadding = `padding-top: 10px; padding-bottom: 10px;`;

          td1Style += ` padding-left: 10px; padding-top: 10px; padding-bottom: 10px;`;
          td2Style += ` padding-right: 10px; padding-top: 10px; padding-bottom: 10px;`;
        }

        // 10px physical spacer columns for absolute alignment regardless of highlight status
        const spacerTd = `<td width="10" style="width: 10px; min-width: 10px; ${vPadding}"></td>`;

        if (isEven) {
          rowsHtml += `
            <table style="${tableStyle}">
              <tr${trBreakStyle}>
                ${spacerTd}
                <td width="${contentLeftColWidth}" style="${td1Style}">
                  ${imgContentLeft}
                </td>
                <td style="${td2Style}">
                  <div class="rte-content" style="margin-top: 8pt; margin-bottom: 8pt; color: #7F7F7F;">
                    ${targetDescription}
                  </div>
                </td>
                ${spacerTd}
              </tr>
            </table>
          `;
        } else {
          rowsHtml += `
            <table style="${tableStyle}">
              <tr${trBreakStyle}>
                ${spacerTd}
                <td style="${td1Style}">
                  <div class="rte-content" style="margin-top: 8pt; margin-bottom: 8pt; color: #7F7F7F;">
                    ${targetDescription}
                  </div>
                </td>
                <td width="${contentLeftColWidth}" style="${td2Style}">
                  ${imgContentRight}
                </td>
                ${spacerTd}
              </tr>
            </table>
          `;
        }
      }
    }

    let infoFilterHtml = '';
    if (infoFilterA || infoFilterB) {
      if (infoFilterA && infoFilterB) {
        infoFilterHtml += `
          <tr><td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Info Filter:</p></td><td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${infoFilterA}</p></td></tr>
          <tr><td style="width: 55px; vertical-align: top; padding: 0;"></td><td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">AND</p></td></tr>
          <tr><td style="width: 55px; vertical-align: top; padding: 0;"></td><td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${infoFilterB}</p></td></tr>
        `;
      } else {
        infoFilterHtml += `<tr><td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Info Filter:</p></td><td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${infoFilterA || infoFilterB}</p></td></tr>`;
      }
    }

    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          @page Section1 {
            mso-footer: f1;
          }
          div.Section1 { page: Section1; }

          body { font-family: ${fallbackFont}; font-size: ${defaultFontSize}; }
          table { width: 100%; border-collapse: collapse; }
          td { vertical-align: top; }
          p { margin-top: 0pt; margin-bottom: 3pt; line-height: 1.2; }
          ul, ol { margin-top: 0pt; margin-bottom: 3pt; padding-left: 20pt; }
          li { margin-bottom: 3pt; line-height: 1.1; color: #7F7F7F; }
          .rte-content p { margin-bottom: 3pt; }
          [style*="margin-left: 40px"] { margin-left: 30pt !important; }
          [style*="margin-left: 80px"] { margin-left: 60pt !important; }
          [style*="margin-left: 120px"] { margin-left: 90pt !important; }
          [style*="margin-left: 160px"] { margin-left: 120pt !important; }

          table#hrdftrtbl {
            margin: 0in 0in 0in 900in;
            width: 1px;
            height: 1px;
            overflow: hidden;
          }
          p.MsoFooter {
            text-align: right; 
            font-family: ${fallbackFont}; 
            font-size: 9pt; 
            margin: 0; 
            color: #000000;
          }
        </style>
      </head>
      <body>
        
        <div class="Section1">
          <table style="width: 100%;">
            <tr>
              <td width="${headerLeftColWidth}" style="width: ${headerLeftColWidth}px; border-bottom: 2px solid black; padding-bottom: 6pt; vertical-align: top;">
                <p style="margin: 0; margin-bottom: 6pt;">${logoHtml}</p>
              </td>
              <td style="text-align: left; border-bottom: 2px solid black; padding-bottom: 6pt; vertical-align: top;">
                <p style="font-family: ${fallbackFont}; font-size: 16pt; font-weight: bold; margin: 0; margin-bottom: 3pt;">${displayTitle}</p>
                
                <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin: 0; font-family: ${fallbackFont};">
                  <tr>
                    <td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Region:</p></td>
                    <td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${regionText}</p></td>
                  </tr>
                  <tr>
                    <td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Category:</p></td>
                    <td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${categoryText}</p></td>
                  </tr>
                  <tr>
                    <td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Date:</p></td>
                    <td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${dateText}</p></td>
                  </tr>
                  ${
                    batchText
                      ? `<tr><td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Batch:</p></td><td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${batchText}</p></td></tr>`
                      : ''
                  }
                  ${
                    keyword
                      ? `<tr><td style="width: 55px; vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: bold; font-size: 7pt;">Keyword:</p></td><td style="vertical-align: top; padding: 0;"><p style="margin: 0pt; mso-line-height-rule: exactly; line-height: 1; font-weight: normal; font-size: 7pt;">${keyword}</p></td></tr>`
                      : ''
                  }
                  ${infoFilterHtml}
                </table>
              </td>
            </tr>
          </table>

          <p style="margin-top: 3pt; margin-bottom: 3pt; line-height: 0; font-size: 1pt;">&nbsp;</p>

          <div style="width: 100%;">
            ${rowsHtml}
          </div>

          <table id="hrdftrtbl" border="0" cellspacing="0" cellpadding="0">
            <tr>
              <td>
                <div style="mso-element:footer" id="f1">
                  <p class="MsoFooter">
                    Page <span style="mso-field-code:' PAGE '"></span> of <span style="mso-field-code:' NUMPAGES '"></span>
                  </p>
                </div>
              </td>
            </tr>
          </table>
        </div>

      </body>
      </html>
    `;

    const buffer = await asBlob(fullHtml, {
      orientation: 'portrait',
      margins: { top: 720, right: 720, bottom: 720, left: 720 },
    });

    saveAs(buffer, `${title || 'Portfolio'}.docx`);
  } catch (error) {
    console.error('Error generating DOCX:', error);
    throw error;
  }
};
